import { useState, type CSSProperties } from 'react';
import { smsSegments } from '../../lib/sms-text';

/**
 * Reply box for a text conversation (JT1-1859). The API sends it from the number the conversation
 * used. No subject, and no opt-out line: it's a one-to-one answer to someone who texted.
 */
export function SmsReplyComposer({
  optedOut,
  sending,
  onSend,
}: {
  optedOut: boolean;
  sending: boolean;
  onSend: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const segments = smsSegments(body);

  const send = async () => {
    setError(null);
    try {
      await onSend(body.trim());
      setBody('');
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? "The text didn't send. Try again in a moment.");
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--color-border-default)', backgroundColor: 'var(--color-surface-white)', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {optedOut && (
        <div role="alert" style={noticeStyle}>
          This person has opted out of texts, so you can&rsquo;t reply by text. If they&rsquo;ve asked to hear from you
          again, an admin can remove them from Text opt-outs in Settings.
        </div>
      )}
      {error && !optedOut && <div role="alert" style={noticeStyle}>{error}</div>}
      <textarea
        aria-label="Text reply"
        value={body}
        disabled={optedOut || sending}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a text reply"
        rows={3}
        maxLength={1600}
        style={textareaStyle}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {body.length} characters · {segments} text{segments === 1 ? '' : 's'}
        </span>
        <button type="button" onClick={send} disabled={optedOut || sending || !body.trim()} style={buttonStyle(optedOut || sending || !body.trim())}>
          {sending ? 'Sending…' : 'Send text'}
        </button>
      </div>
    </div>
  );
}

const noticeStyle: CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-input)',
  border: '1px solid var(--color-warning)',
  color: 'var(--color-warning)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm)',
};
const textareaStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--space-3)',
  border: '1px solid var(--input-border)',
  borderRadius: 'var(--input-radius)',
  backgroundColor: 'var(--input-bg)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--input-font-size)',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
  boxSizing: 'border-box',
};
const buttonStyle = (disabled: boolean): CSSProperties => ({
  height: 'var(--btn-height)',
  padding: '0 var(--space-4)',
  border: 'none',
  borderRadius: 'var(--btn-radius)',
  backgroundColor: 'var(--color-action-primary)',
  color: 'var(--color-action-primary-text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});
